import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Messages } from "primereact/messages";
import { useState, useRef } from "react";
import StatusTag from "../../components/StatusTag";
import { CaseStatus } from "../../constants";
import { BASE_URL } from "../../constants";
import { Toast } from "primereact/toast";

export default function ConciliarCaseDialog({
  show,
  onHide,
  caseMutation,
  caseId,
  onFileUploaded // <- Añade esta prop
}) {
  const [observation, setObservation] = useState("");
  const [supportFile, setSupportFile] = useState(null);
  const [supportFileError, setSupportFileError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const toastRef = useRef(null);
  const msgs = useRef(null);

  const validateAndSetFile = (file) => {
    const TAMANO_MAXIMO_BYTES = 1 * 1024 * 1024; // 1MB
    const TIPOS_PERMITIDOS = ["application/pdf", "image/jpeg", "image/png"];

    if (!TIPOS_PERMITIDOS.includes(file.type)) {
      setSupportFileError("Formato no permitido. Use PDF, JPG o PNG.");
      setSupportFile(null);
      return;
    }

    if (file.size > TAMANO_MAXIMO_BYTES) {
      setSupportFileError("El archivo no debe superar los 1MB.");
      setSupportFile(null);
      return;
    }

    setSupportFile(file);
    setSupportFileError("");
  };

  const onValidationFail = () => {
    setSupportFileError("Archivo inválido");
    msgs.current.show([
      {
        sticky: true,
        severity: "error",
        summary: "Tamaño o formato incorrecto",
        detail: "El archivo debe ser PDF, JPG o PNG y no superar 1MB.",
      },
    ]);
  };

  const handleOnHide = () => {
    onHide();
    setObservation("");
    setSupportFile(null);
    setSupportFileError("");
  };

  
const handleSubmit = async () => {
  setIsUploading(true);
  
  try {
    let uploadedFile = null;
    
    if (supportFile) {
      const formDataDoc = new FormData();
      formDataDoc.append("file", supportFile);
      formDataDoc.append("medical_case", caseId);

      const token = localStorage.getItem('token');
      const uploadResponse = await fetch(`${BASE_URL}insurance/medical-case-conciliate/`, {
        method: "POST",
        body: formDataDoc,
        headers: {
          'Authorization': `Token ${token}` 
        }
      });

      if (!uploadResponse.ok) throw new Error("Error al subir el documento");
      
      uploadedFile = await uploadResponse.json();
      if (onFileUploaded) onFileUploaded(uploadedFile);

      // Guardar en localStorage
      const storedFiles = JSON.parse(localStorage.getItem(`conciliationFiles_${caseId}`)) || [];
      storedFiles.push(uploadedFile);
      localStorage.setItem(`conciliationFiles_${caseId}`, JSON.stringify(storedFiles));
    }

    await caseMutation.mutateAsync({
      caseId: caseId,
      data: {
        status: CaseStatus.CONCILIADO,
        update_observation: observation || "",
      },
    });

    toastRef.current.show({
      severity: "success",
      summary: "Éxito",
      detail: "Documento y estado del caso actualizados correctamente",
    });

    handleOnHide();
  } catch (err) {
    console.error("Error en handleSubmit:", err);
    toastRef.current.show({
      severity: "error",
      summary: "Error",
      detail: err.message || "Hubo un problema al procesar su solicitud.",
    });
  } finally {
    setIsUploading(false);
  }
};

  return (
    <Dialog
      header="Conciliar"
      visible={show}
      style={{ maxWidth: "35rem" }}
      onHide={handleOnHide}
      draggable={false}
    >
      <div className="p-fluid text-center">
        <p>
          <b>
            Esto cambiará el estado del caso de{" "}
            <StatusTag value={CaseStatus.REMESADO} /> a{" "}
            <StatusTag value={CaseStatus.CONCILIADO} />
          </b>
        </p>
      </div>

      {/* Observación */}
      <div className="p-field flex flex-column mt-3">
        <label htmlFor="observation">Observación (opcional)</label>
        <textarea
          id="observation"
          value={observation}
          onChange={(e) => setObservation(e.target.value)}
          className="p-inputtext p-component"
          rows="3"
          placeholder="Escriba una observación"
        ></textarea>
      </div>

      {/* Soporte de documento */}
      <div className="p-field flex flex-column mt-3">
        <label>Adjuntar documento (máximo 1MB)</label>
        <Messages ref={msgs} />

        <FileUpload
          mode="basic"
          name="documento"
          auto={false}
          accept=".pdf,.jpeg,.jpg,.png"
          maxFileSize={1000000}
          onSelect={(e) => {
            if (e.files.length > 0) {
              validateAndSetFile(e.files[0]);
            }
          }}
          onValidationFail={onValidationFail}
          customUpload={true}
          chooseLabel="Seleccionar archivo"
        />

        {supportFile && (
          <small className="mt-2" style={{ color: "green" }}>
            Archivo seleccionado: {supportFile.name} ({(supportFile.size / 1024).toFixed(2)} KB)
          </small>
        )}

        {supportFileError && (
          <small className="p-error mt-2">{supportFileError}</small>
        )}
      </div>

      <br />

      {/* Botones */}
      <div className="delete-confirm-buttons-container">
        <Button
          label="Confirmar"
          loading={isUploading || caseMutation.isPending}
          disabled={isUploading || caseMutation.isPending || supportFileError !== "" || !supportFile || !observation}
          onClick={handleSubmit}
        />
        <Button
          label="Cancelar"
          style={{ backgroundColor: "#999999", borderColor: "#999999" }}
          disabled={isUploading || caseMutation.isPending}
          onClick={handleOnHide}
        />
      </div>

      <Toast ref={toastRef} position="top-center" />
    </Dialog>
  );
}